import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSessionToken, useIsGuest, useSessionRoutes, usePageHeaderAction } from '@/hooks';
import { usePageBackLink } from '@/hooks';
import { Spin, Alert, Typography, Tag, Card } from 'antd';
import { ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { HtsSubmissionSummaryStatus } from '@evaltick/shared';
import { HtsService, InterviewTemplatesService, AssessmentService, BaseAPIClient } from '@/services/api';
import type { HtsReadinessSummary, InterviewTemplate } from '@/services/api';
import { NextPartModal, wasPrompted, markPrompted } from '@/components/NextPartModal';
import { partsOf, remainingPart, remainingPartCopy, type TemplatePart } from '@/pages/Sessions/interviewStatus';
import { Button as BrandButton } from '@/components/Button';
import { useSessionWizardStore } from '@/store';
import styles from './HtsReadiness.module.scss';

const { Title, Paragraph, Text } = Typography;

// Generous over any realistic submission count — just needs to contain this
// page's own (newest) submissionId to resolve its interviewTemplateId.
const SUBMISSIONS_LOOKUP_PAGE_SIZE = 50;

/**
 * The candidate's Readiness Profile summary, shown once after a submission
 * is finalized (see HtsQuestionnaire.tsx's handleSubmitConfirm). Numbers-free
 * by design — the API only ever returns a band and pillar labels, never the
 * composite score or a dimension name (see
 * htsReadinessScoring.ts's getHtsReadinessSummaryForCandidate).
 *
 * One HTS attempt per template, ever — there is no "Try Again" here anymore.
 */
export const HtsReadiness: React.FC = () => {
  const navigate = useNavigate();
  const { submissionId } = useParams<{ submissionId: string }>();
  const { getToken } = useSessionToken();
  const isGuest = useIsGuest();
  const sessionRoutes = useSessionRoutes();
  const resetWizard = useSessionWizardStore((s) => s.resetWizard);
  const enterTemplate = useSessionWizardStore((s) => s.enterTemplate);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [summary, setSummary] = useState<HtsReadinessSummary | null>(null);
  // Needed for a guest to know whether the paired competency interview is
  // still outstanding on this template (see the CTA below).
  const [assessmentEverCompleted, setAssessmentEverCompleted] = useState(false);
  // This submission's template — lets Back return to it rather than an empty
  // setup card, and drives the one-time "what's next" prompt.
  const [template, setTemplate] = useState<InterviewTemplate | null>(null);
  const [nextPart, setNextPart] = useState<TemplatePart | null>(null);

  useEffect(() => {
    if (!submissionId) {
      navigate(sessionRoutes.hub, { replace: true });
      return;
    }
    let cancelled = false;

    const findOwnTemplate = async (token: string): Promise<InterviewTemplate | null> => {
      const [mySubmissions, templates] = await Promise.all([
        new HtsService(token).listMySubmissions(1, SUBMISSIONS_LOOKUP_PAGE_SIZE),
        new InterviewTemplatesService(token).list(),
      ]);
      const interviewTemplateId = mySubmissions.data.find((s) => s.id === submissionId)?.interviewTemplateId;
      return templates.find((t) => t.id === interviewTemplateId) ?? null;
    };

    // Signed-in only, and never awaited by the summary load: it feeds just
    // the Back target and the optional "what's next" prompt, so it must not
    // delay or fail the page. partsOf can't tell an open Competency sitting
    // from an untouched (or previously completed) one without the history
    // list, so that one signal is asked for directly.
    const loadNextStep = async (token: string) => {
      try {
        const ownTemplate = await findOwnTemplate(token);
        if (cancelled || !ownTemplate) return;
        setTemplate(ownTemplate);
        if (wasPrompted(submissionId)) return;
        const openSittingId = await new AssessmentService(token).findActiveAssessmentId(ownTemplate.id);
        if (cancelled) return;
        const parts = partsOf(ownTemplate, new Map()).map((p) =>
          p.key === 'comp' && openSittingId ? { ...p, status: 'prog' as const } : p
        );
        setNextPart(remainingPart(parts, 'hts'));
      } catch (err) {
        console.error('Next-step lookup failed:', BaseAPIClient.parseErrorMsg(err));
      }
    };

    (async () => {
      try {
        const token = await getToken();
        if (!token) throw new Error('Not authenticated');
        if (isGuest) {
          // A guest's header CTA depends on the template, so it loads with
          // the summary (and a failure fails the page, as before).
          const [summaryResult, ownTemplate] = await Promise.all([
            new HtsService(token).getReadinessSummary(submissionId),
            findOwnTemplate(token),
          ]);
          if (cancelled) return;
          setSummary(summaryResult);
          setTemplate(ownTemplate);
          setAssessmentEverCompleted(ownTemplate?.assessmentEverCompleted ?? false);
          return;
        }
        const summaryResult = await new HtsService(token).getReadinessSummary(submissionId);
        if (cancelled) return;
        setSummary(summaryResult);
        void loadNextStep(token);
      } catch (err) {
        if (!cancelled) setLoadError(BaseAPIClient.parseErrorMsg(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [submissionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPendingReview = summary?.status === HtsSubmissionSummaryStatus.PendingReview;
  // An access-code guest's result — never shown to them; the hub carries the thank-you message.
  const isWithheld = summary?.status === HtsSubmissionSummaryStatus.WithheldFromCandidate;

  useEffect(() => {
    if (isWithheld) navigate(sessionRoutes.hub, { replace: true });
  }, [isWithheld, navigate, sessionRoutes]);

  const handleBack = () => {
    if (template) enterTemplate(template, null);
    else resetWizard();
    navigate(sessionRoutes.hub);
  };

  const closeNextPart = () => {
    if (submissionId) markPrompted(submissionId);
    setNextPart(null);
  };

  const startNextPart = () => {
    if (!template || !nextPart) return;
    closeNextPart();
    enterTemplate(template, nextPart.key);
    navigate(sessionRoutes.hub);
  };

  // HTS is one-shot — there's never a "Try Again" here, only a way to reach
  // the Competency Questionnaire if it's still outstanding on this template.
  const startCompetency = () => {
    if (!template) return;
    enterTemplate(template, 'comp');
    navigate(sessionRoutes.hub);
  };
  usePageBackLink(handleBack, 'Back to Sessions');

  // Guests get no back link in MainLayout's header (it shows the wordmark
  // instead), so this is their only way off this page that isn't ending the
  // session. It always goes to the hub: that's where either flow actually
  // starts, and the guest's session is still live, so there's nothing to
  // re-establish via /redeem. Labelled for the competency interview while
  // that's still outstanding on this template, since that's the step the hub
  // will land them on.
  usePageHeaderAction(
    isGuest ? (
      <BrandButton appearance="solid" size="small" onClick={() => navigate(sessionRoutes.hub)}>
        {assessmentEverCompleted ? 'Back to Sessions' : 'Competency Interview'}
      </BrandButton>
    ) : null
  );

  if (loading || isWithheld) {
    return (
      <div className={styles.page}>
        <div className={styles.centerSpin}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (loadError || !summary) {
    return (
      <div className={styles.page}>
        <Alert type="error" showIcon message={loadError ?? 'Your readiness summary is not available yet.'} />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <button type="button" className={styles.backLink} onClick={handleBack}>
        <ArrowLeft size={16} />
        Back to Sessions
      </button>

      <div className={styles.body}>
        <motion.div
          className={styles.hero}
          initial={{ y: -16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <div className={styles.heroText}>
            <Title level={2} className={styles.heroTitle}>
              Readiness Summary
            </Title>
            <Text type="secondary" className={styles.heroSub}>
              HTS Questionnaire
            </Text>
          </div>
          {template && !template.assessmentEverCompleted && (
            <div className={styles.heroActions}>
              <BrandButton appearance="solid" onClick={startCompetency}>
                Start Competency
              </BrandButton>
            </div>
          )}
        </motion.div>

        <div className={styles.content}>
          {(summary.bandLabel != null || summary.provisional || isPendingReview) && (
            <Card className={styles.summaryCard}>
              {summary.bandLabel != null && (
                <div className={styles.scoreRow}>
                  <Title level={4} className={styles.scoreSublabel}>Overall readiness score range</Title>
                  <Tag>{summary.bandLabel}</Tag>
                </div>
              )}

              {(summary.provisional || isPendingReview) && (
                <Alert
                  type="info"
                  showIcon
                  className={styles.provisionalAlert}
                  // Two situations share this branch: a real band under review, or no
                  // band because scoring itself needs a human. Absence of a band is
                  // what separates them (same message).
                  message={
                    summary.bandLabel != null
                      ? 'This summary is provisional — one item is still pending human review, so it may be updated.'
                      : "Your readiness result isn't available yet — this submission needs a manual review before it can be scored."
                  }
                />
              )}
            </Card>
          )}

          {!!summary.strengths?.length && (
            <Card className={styles.sectionCard}>
              <Title level={4} className={styles.sectionTitle}>Where you're strong</Title>
              <div className={styles.tagRow}>
                {summary.strengths.map((label) => (
                  <Tag key={label} color="success">{label}</Tag>
                ))}
              </div>
              {summary.narrative && <Paragraph className={styles.narrative}>{summary.narrative.strengths}</Paragraph>}
            </Card>
          )}

          {!!summary.priorities?.length && (
            <Card className={styles.sectionCard}>
              <Title level={4} className={styles.sectionTitle}>Where to focus next</Title>
              <div className={styles.tagRow}>
                {summary.priorities.map((label) => (
                  <Tag key={label} color="warning">{label}</Tag>
                ))}
              </div>
              {summary.narrative && <Paragraph className={styles.narrative}>{summary.narrative.priorities}</Paragraph>}
            </Card>
          )}

          {summary.narrative && (
            <Card className={styles.sectionCard}>
              <Title level={4} className={styles.sectionTitle}>The bottom line</Title>
              <Paragraph className={styles.narrative}>{summary.narrative.bottomLine}</Paragraph>
            </Card>
          )}
        </div>
      </div>

      {nextPart && (
        <NextPartModal
          title="HTS Questionnaire submitted"
          body={
            isPendingReview
              ? 'Your summary is being reviewed. In the meantime, this interview has one more questionnaire to finish.'
              : 'Your Readiness Summary is ready. This interview has one more questionnaire to finish.'
          }
          finishedLabel="HTS Questionnaire"
          finishedDetail="Submitted"
          nextLabel={nextPart.label}
          nextDetail={remainingPartCopy(nextPart).detail}
          nextNotStarted={nextPart.status === 'new'}
          primaryLabel={remainingPartCopy(nextPart).primaryLabel}
          dismissLabel={isPendingReview ? 'Not now' : 'View Results'}
          onPrimary={startNextPart}
          onDismiss={closeNextPart}
        />
      )}
    </div>
  );
};
